import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch } from '../lib/apiClient';

type HuntingSource = 'sentinel' | 'defender';
type QueryExecutionMode = 'standard' | 'set_query_now' | 'custom';
type TimeRangeMode = 'lookback' | 'absolute' | 'in_query';

export interface AdvancedHuntingSavedQuery {
  id: string;
  customerId: string;
  name: string;
  description?: string;
  queryText: string;
  source: HuntingSource;
  queryExecutionMode?: QueryExecutionMode;
  timeRangeMode?: TimeRangeMode;
  timespan?: string;
  resourceType?: string;
  category?: string;
  labels?: string[];
  visibility?: string;
  updatedAtUtc?: string;
  createdAtUtc?: string;
  createdBy?: { name?: string | null; email?: string | null };
}

export interface AdvancedHuntingSavedFunction {
  id: string;
  customerId: string;
  name: string;
  description?: string;
  functionText: string;
  queryText?: string;
  source: HuntingSource;
  queryExecutionMode?: QueryExecutionMode;
  timeRangeMode?: TimeRangeMode;
  timespan?: string;
  category?: string;
  labels?: string[];
  visibility?: string;
  updatedAtUtc?: string;
  createdAtUtc?: string;
  createdBy?: { name?: string | null; email?: string | null };
}

export interface AdvancedHuntingHistoryItem {
  id: string;
  title: string;
  source: HuntingSource;
  customerId: string;
  customerName?: string;
  queryText: string;
  queryExecutionMode: QueryExecutionMode;
  timeRangeMode: TimeRangeMode;
  timespan?: string;
  limit: number;
  rowCount?: number;
  durationMs?: number;
  ranAtUtc: string;
}

interface LoadQueryPayload {
  queryText: string;
  source: HuntingSource;
  queryExecutionMode?: QueryExecutionMode;
  timeRangeMode?: TimeRangeMode;
  timespan?: string;
  limit?: number;
  name?: string;
}

interface ExplorerItem {
  id: string;
  title: string;
  subtitle?: string;
  queryText: string;
  source: HuntingSource;
  badge?: string;
}

interface FunctionSnippet extends ExplorerItem {
  description: string;
}

interface AdvancedHuntingExplorerProps {
  customerId: string;
  customerName?: string;
  source: HuntingSource;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onLoadQuery: (payload: LoadQueryPayload) => void;
  queryHistory: AdvancedHuntingHistoryItem[];
  onClearHistory: () => void;
  onDeleteHistoryItem: (id: string) => void;
  canViewTables: boolean;
  canViewFunctions: boolean;
  canViewSavedFunctions: boolean;
  canViewSavedQueries: boolean;
  canViewQueryHistory: boolean;
  canManageQueryHistory: boolean;
}

const SENTINEL_TABLES: ExplorerItem[] = [
  table('SecurityIncident', 'Microsoft Sentinel incidents', 'sentinel'),
  table('SecurityAlert', 'Security alerts linked to incidents', 'sentinel'),
  table('SecurityEvent', 'Windows security event logs', 'sentinel'),
  table('CommonSecurityLog', 'Firewall and CEF security logs', 'sentinel'),
  table('Syslog', 'Linux/network syslog records', 'sentinel'),
  table('SigninLogs', 'Microsoft Entra interactive sign-ins', 'sentinel'),
  table('AuditLogs', 'Microsoft Entra audit records', 'sentinel'),
  table('OfficeActivity', 'Microsoft 365 audit activity', 'sentinel'),
  table('AzureActivity', 'Azure control-plane activity', 'sentinel'),
  table('Heartbeat', 'Agent heartbeat and computer inventory', 'sentinel'),
  table('Usage', 'Log Analytics ingestion usage', 'sentinel'),
  table('ThreatIntelIndicators', 'Threat intelligence indicators', 'sentinel'),
  table('ThreatIntelligenceIndicator', 'Legacy threat intelligence indicators', 'sentinel'),
  table('DeviceEvents', 'Endpoint device events where available in workspace', 'sentinel'),
];

const DEFENDER_TABLES: ExplorerItem[] = [
  table('DeviceInfo', 'Device inventory and latest device posture', 'defender'),
  table('DeviceProcessEvents', 'Process execution events', 'defender'),
  table('DeviceNetworkEvents', 'Endpoint network connections', 'defender'),
  table('DeviceFileEvents', 'File creation/modification events', 'defender'),
  table('DeviceRegistryEvents', 'Registry modification events', 'defender'),
  table('DeviceLogonEvents', 'Endpoint logon activity', 'defender'),
  table('EmailEvents', 'Defender for Office 365 email events', 'defender'),
  table('EmailUrlInfo', 'URL details extracted from email', 'defender'),
  table('UrlClickEvents', 'Safe Links URL click events', 'defender'),
  table('AlertInfo', 'Defender XDR alert metadata', 'defender'),
  table('AlertEvidence', 'Defender XDR alert evidence', 'defender'),
  table('IdentityLogonEvents', 'Identity logon events', 'defender'),
  table('CloudAppEvents', 'Defender for Cloud Apps events', 'defender'),
];

const FUNCTION_SNIPPETS: FunctionSnippet[] = [
  {
    id: 'ks-extract-domain',
    title: 'Extract domain from URL',
    subtitle: 'Reusable URL parsing helper',
    source: 'sentinel',
    badge: 'let',
    description: 'Adds a small function to extract a domain/host from a URL string.',
    queryText: `let KS_ExtractDomainFromUrl = (url:string) {
  tostring(parse_url(url).Host)
};
// Example:
SecurityAlert
| extend ExtractedDomain = KS_ExtractDomainFromUrl(tostring(ExtendedProperties["Url"]))
| take 50`,
  },
  {
    id: 'ks-normalize-account',
    title: 'Normalize account name',
    subtitle: 'Lowercase and trim domain prefixes',
    source: 'sentinel',
    badge: 'let',
    description: 'Normalises account strings before summarizing or joining.',
    queryText: `let KS_NormalizeAccount = (account:string) {
  tolower(trim(@"\\", tostring(split(account, "\\")[-1])))
};
SigninLogs
| extend NormalizedAccount = KS_NormalizeAccount(UserPrincipalName)
| summarize Count=count() by NormalizedAccount
| top 50 by Count desc`,
  },
  {
    id: 'ks-recent-alerts',
    title: 'Recent Sentinel alerts function',
    subtitle: 'Parameterised alert helper',
    source: 'sentinel',
    badge: 'fn',
    description: 'Creates a parameterised helper for recent Sentinel SecurityAlert rows.',
    queryText: `let KS_RecentSecurityAlerts = (lookback:timespan) {
  SecurityAlert
  | where TimeGenerated >= ago(lookback)
  | project TimeGenerated, AlertName, AlertSeverity, ProviderName, CompromisedEntity, Entities
};
KS_RecentSecurityAlerts(24h)
| order by TimeGenerated desc
| take 100`,
  },
  {
    id: 'ks-defender-process-chain',
    title: 'Defender process chain helper',
    subtitle: 'Endpoint parent/child process context',
    source: 'defender',
    badge: 'fn',
    description: 'Projects process execution fields commonly required during endpoint triage.',
    queryText: `let KS_ProcessChain = (lookback:timespan) {
  DeviceProcessEvents
  | where Timestamp >= ago(lookback)
  | project Timestamp, DeviceName, AccountName, FileName, ProcessCommandLine, InitiatingProcessFileName, InitiatingProcessCommandLine, SHA256
};
KS_ProcessChain(24h)
| order by Timestamp desc
| take 100`,
  },
  {
    id: 'ks-defender-network-summary',
    title: 'Defender network summary helper',
    subtitle: 'Summarize remote connections',
    source: 'defender',
    badge: 'fn',
    description: 'Summarizes remote IP/URL connection activity over a selected lookback.',
    queryText: `let KS_DeviceNetworkSummary = (lookback:timespan) {
  DeviceNetworkEvents
  | where Timestamp >= ago(lookback)
  | summarize Connections=count(), Devices=dcount(DeviceName), Processes=make_set(InitiatingProcessFileName, 10) by RemoteIP, RemoteUrl, RemotePort, ActionType
};
KS_DeviceNetworkSummary(24h)
| top 100 by Connections desc`,
  },
];

function table(name: string, subtitle: string, source: HuntingSource): ExplorerItem {
  const timeColumn = source === 'defender' ? 'Timestamp' : 'TimeGenerated';
  return {
    id: `${source}-${name}`,
    title: name,
    subtitle,
    source,
    badge: 'table',
    queryText: `${name}\n| order by ${timeColumn} desc\n| take 50`,
  };
}

function formatDateTime(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function shortQuery(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, 140);
}

function sourceLabel(source: HuntingSource): string {
  return source === 'defender' ? 'Defender XDR' : 'Sentinel';
}

function normalizeSavedQueriesResponse(value: unknown): AdvancedHuntingSavedQuery[] {
  const record = value && typeof value === 'object' ? value as { queries?: unknown } : {};
  if (!Array.isArray(record.queries)) return [];
  return record.queries
    .map((item): AdvancedHuntingSavedQuery | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const source = row.source === 'defender' ? 'defender' : 'sentinel';
      const queryText = String(row.queryText ?? row.query ?? '').trim();
      const name = String(row.name ?? '').trim();
      if (!queryText || !name) return null;
      return {
        id: String(row.id ?? `${source}-${name}`),
        customerId: String(row.customerId ?? ''),
        name,
        description: String(row.description ?? ''),
        queryText,
        source,
        queryExecutionMode: row.queryExecutionMode === 'set_query_now' || row.queryExecutionMode === 'custom' ? row.queryExecutionMode : 'standard',
        timeRangeMode: row.timeRangeMode === 'absolute' || row.timeRangeMode === 'in_query' ? row.timeRangeMode : 'lookback',
        timespan: typeof row.timespan === 'string' ? row.timespan : undefined,
        resourceType: typeof row.resourceType === 'string' ? row.resourceType : undefined,
        category: typeof row.category === 'string' ? row.category : undefined,
        labels: Array.isArray(row.labels) ? row.labels.map(String) : [],
        visibility: typeof row.visibility === 'string' ? row.visibility : undefined,
        createdAtUtc: typeof row.createdAtUtc === 'string' ? row.createdAtUtc : undefined,
        updatedAtUtc: typeof row.updatedAtUtc === 'string' ? row.updatedAtUtc : undefined,
        createdBy: row.createdBy && typeof row.createdBy === 'object' ? row.createdBy as AdvancedHuntingSavedQuery['createdBy'] : undefined,
      };
    })
    .filter((item): item is AdvancedHuntingSavedQuery => item !== null);
}

function normalizeSavedFunctionsResponse(value: unknown): AdvancedHuntingSavedFunction[] {
  const record = value && typeof value === 'object' ? value as { functions?: unknown } : {};
  if (!Array.isArray(record.functions)) return [];
  return record.functions
    .map((item): AdvancedHuntingSavedFunction | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const source = row.source === 'defender' ? 'defender' : 'sentinel';
      const functionText = String(row.functionText ?? row.queryText ?? '').trim();
      const name = String(row.name ?? '').trim();
      if (!functionText || !name) return null;
      return {
        id: String(row.id ?? `${source}-${name}`),
        customerId: String(row.customerId ?? ''),
        name,
        description: String(row.description ?? ''),
        functionText,
        queryText: functionText,
        source,
        queryExecutionMode: row.queryExecutionMode === 'set_query_now' || row.queryExecutionMode === 'custom' ? row.queryExecutionMode : 'standard',
        timeRangeMode: row.timeRangeMode === 'absolute' || row.timeRangeMode === 'lookback' ? row.timeRangeMode : 'in_query',
        timespan: typeof row.timespan === 'string' ? row.timespan : undefined,
        category: typeof row.category === 'string' ? row.category : undefined,
        labels: Array.isArray(row.labels) ? row.labels.map(String) : [],
        visibility: typeof row.visibility === 'string' ? row.visibility : undefined,
        createdAtUtc: typeof row.createdAtUtc === 'string' ? row.createdAtUtc : undefined,
        updatedAtUtc: typeof row.updatedAtUtc === 'string' ? row.updatedAtUtc : undefined,
        createdBy: row.createdBy && typeof row.createdBy === 'object' ? row.createdBy as AdvancedHuntingSavedFunction['createdBy'] : undefined,
      };
    })
    .filter((item): item is AdvancedHuntingSavedFunction => item !== null);
}

function normalizeQueryHistoryResponse(value: unknown): AdvancedHuntingHistoryItem[] {
  const record = value && typeof value === 'object' ? value as { history?: unknown } : {};
  if (!Array.isArray(record.history)) return [];
  return record.history
    .map((item): AdvancedHuntingHistoryItem | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const queryText = String(row.queryText ?? '').trim();
      if (!queryText) return null;
      return {
        id: String(row.id ?? `${Date.now()}`),
        title: String(row.title ?? queryText.split('\n')[0] ?? 'Advanced Hunting query'),
        source: row.source === 'defender' ? 'defender' : 'sentinel',
        customerId: String(row.customerId ?? ''),
        customerName: undefined,
        queryText,
        queryExecutionMode: row.queryExecutionMode === 'set_query_now' || row.queryExecutionMode === 'custom' ? row.queryExecutionMode : 'standard',
        timeRangeMode: row.timeRangeMode === 'absolute' || row.timeRangeMode === 'in_query' ? row.timeRangeMode : 'lookback',
        timespan: typeof row.timespan === 'string' ? row.timespan : undefined,
        limit: Number(row.limit) > 0 ? Number(row.limit) : 500,
        rowCount: Number.isFinite(Number(row.rowCount)) ? Number(row.rowCount) : undefined,
        durationMs: Number.isFinite(Number(row.durationMs)) ? Number(row.durationMs) : undefined,
        ranAtUtc: typeof row.ranAtUtc === 'string' ? row.ranAtUtc : new Date().toISOString(),
      };
    })
    .filter((item): item is AdvancedHuntingHistoryItem => item !== null);
}

function SectionHeader({ title, count, children }: { title: string; count?: number; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1.5">
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {title}{typeof count === 'number' ? <span className="ml-1 text-muted-foreground/70">({count})</span> : null}
      </div>
      {children}
    </div>
  );
}

function ItemButton({ item, onClick }: { item: ExplorerItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg px-2 py-2 text-left hover:bg-muted transition-colors group"
      title={item.queryText}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-foreground group-hover:text-primary">{item.title}</div>
          {item.subtitle ? <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{item.subtitle}</div> : null}
        </div>
        {item.badge ? <span className="shrink-0 rounded-full bg-muted-foreground/10 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{item.badge}</span> : null}
      </div>
    </button>
  );
}

export function AdvancedHuntingExplorer({
  customerId,
  customerName,
  source,
  collapsed,
  onCollapsedChange,
  onLoadQuery,
  queryHistory,
  onClearHistory,
  onDeleteHistoryItem,
  canViewTables,
  canViewFunctions,
  canViewSavedFunctions,
  canViewSavedQueries,
  canViewQueryHistory,
  canManageQueryHistory,
}: AdvancedHuntingExplorerProps) {
  const [activeSection, setActiveSection] = useState<'tables' | 'functions' | 'saved' | 'history'>('tables');
  const [search, setSearch] = useState('');
  const [savedQueries, setSavedQueries] = useState<AdvancedHuntingSavedQuery[]>([]);
  const [savedFunctions, setSavedFunctions] = useState<AdvancedHuntingSavedFunction[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState<string | null>(null);
  const [functionsLoading, setFunctionsLoading] = useState(false);
  const [functionsError, setFunctionsError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [serverHistory, setServerHistory] = useState<AdvancedHuntingHistoryItem[]>([]);
  const [savedRefreshToken, setSavedRefreshToken] = useState(0);
  const [functionsRefreshToken, setFunctionsRefreshToken] = useState(0);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);

  const tables = source === 'defender' ? DEFENDER_TABLES : SENTINEL_TABLES;
  const builtInFunctions = FUNCTION_SNIPPETS.filter((item) => item.source === source);
  const savedFunctionItems = savedFunctions
    .filter((item) => item.source === source)
    .map((item): FunctionSnippet => ({
      id: `saved-${item.id}`,
      title: item.name,
      subtitle: [item.category || item.visibility || 'Saved function', item.updatedAtUtc ? formatDateTime(item.updatedAtUtc) : ''].filter(Boolean).join(' · '),
      source: item.source,
      badge: 'saved fn',
      description: item.description || 'Saved reusable Advanced Hunting function.',
      queryText: item.functionText,
    }));
  const functions = [...savedFunctionItems, ...builtInFunctions];
  const historyItems = serverHistory.length > 0 ? serverHistory : queryHistory;

  const visibleSections = useMemo(() => [
    { id: 'tables' as const, label: 'Tables', enabled: canViewTables, count: tables.length },
    { id: 'functions' as const, label: 'Functions', enabled: canViewFunctions || canViewSavedFunctions, count: functions.length },
    { id: 'saved' as const, label: 'Saved Queries', enabled: canViewSavedQueries, count: savedQueries.length },
    { id: 'history' as const, label: 'History', enabled: canViewQueryHistory, count: historyItems.length },
  ].filter((section) => section.enabled), [canViewFunctions, canViewSavedFunctions, canViewQueryHistory, canViewSavedQueries, canViewTables, functions.length, historyItems.length, savedQueries.length, tables.length]);

  useEffect(() => {
    if (!visibleSections.some((section) => section.id === activeSection)) {
      setActiveSection(visibleSections[0]?.id ?? 'tables');
    }
  }, [activeSection, visibleSections]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refresh = () => setSavedRefreshToken((prev) => prev + 1);
    window.addEventListener('advanced-hunting-saved-query-created', refresh);
    return () => window.removeEventListener('advanced-hunting-saved-query-created', refresh);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refreshFunctions = () => setFunctionsRefreshToken((prev) => prev + 1);
    window.addEventListener('advanced-hunting-saved-function-created', refreshFunctions);
    return () => window.removeEventListener('advanced-hunting-saved-function-created', refreshFunctions);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refreshHistory = () => setHistoryRefreshToken((prev) => prev + 1);
    window.addEventListener('advanced-hunting-query-history-updated', refreshHistory);
    return () => window.removeEventListener('advanced-hunting-query-history-updated', refreshHistory);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!canViewSavedQueries || !customerId) {
      setSavedQueries([]);
      setSavedError(null);
      setSavedLoading(false);
      return;
    }
    setSavedLoading(true);
    setSavedError(null);
    apiFetch<unknown>(`/advanced-hunting/saved-queries?customerId=${encodeURIComponent(customerId)}&source=${encodeURIComponent(source)}`)
      .then((data) => {
        if (!cancelled) setSavedQueries(normalizeSavedQueriesResponse(data));
      })
      .catch((error) => {
        if (!cancelled) {
          setSavedQueries([]);
          setSavedError(error instanceof Error ? error.message : 'Could not load saved queries.');
        }
      })
      .finally(() => {
        if (!cancelled) setSavedLoading(false);
      });
    return () => { cancelled = true; };
  }, [canViewSavedQueries, customerId, savedRefreshToken, source]);

  useEffect(() => {
    let cancelled = false;
    if (!canViewSavedFunctions || !customerId) {
      setSavedFunctions([]);
      setFunctionsError(null);
      setFunctionsLoading(false);
      return;
    }
    setFunctionsLoading(true);
    setFunctionsError(null);
    apiFetch<unknown>(`/advanced-hunting/functions?customerId=${encodeURIComponent(customerId)}&source=${encodeURIComponent(source)}`)
      .then((data) => {
        if (!cancelled) setSavedFunctions(normalizeSavedFunctionsResponse(data));
      })
      .catch((error) => {
        if (!cancelled) {
          setSavedFunctions([]);
          setFunctionsError(error instanceof Error ? error.message : 'Could not load saved functions.');
        }
      })
      .finally(() => {
        if (!cancelled) setFunctionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [canViewSavedFunctions, customerId, functionsRefreshToken, source]);

  useEffect(() => {
    let cancelled = false;
    if (!canViewQueryHistory || !customerId) {
      setServerHistory([]);
      setHistoryError(null);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    setHistoryError(null);
    apiFetch<unknown>(`/advanced-hunting/query-history?customerId=${encodeURIComponent(customerId)}&source=${encodeURIComponent(source)}`)
      .then((data) => {
        if (!cancelled) setServerHistory(normalizeQueryHistoryResponse(data));
      })
      .catch((error) => {
        if (!cancelled) {
          setServerHistory([]);
          setHistoryError(error instanceof Error ? error.message : 'Could not load server query history. Falling back to browser history.');
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [canViewQueryHistory, customerId, historyRefreshToken, source]);

  async function handleClearHistory() {
    const confirmed = window.confirm('Clear Advanced Hunting query history for this source/customer? This action is audited.');
    if (!confirmed) return;
    if (canManageQueryHistory && customerId) {
      try {
        await apiFetch(`/advanced-hunting/query-history?customerId=${encodeURIComponent(customerId)}&source=${encodeURIComponent(source)}`, { method: 'DELETE' });
        setServerHistory([]);
      } catch (error) {
        setHistoryError(error instanceof Error ? error.message : 'Could not clear query history.');
        return;
      }
    }
    onClearHistory();
  }

  async function handleDeleteHistoryItem(id: string) {
    if (canManageQueryHistory && customerId && serverHistory.some((item) => item.id === id)) {
      try {
        await apiFetch(`/advanced-hunting/query-history/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' });
        setServerHistory((prev) => prev.filter((item) => item.id !== id));
        return;
      } catch (error) {
        setHistoryError(error instanceof Error ? error.message : 'Could not delete query history item.');
        return;
      }
    }
    onDeleteHistoryItem(id);
  }

  const filteredTables = useMemo(() => filterItems(tables, search), [tables, search]);
  const filteredFunctions = useMemo(() => filterItems(functions, search), [functions, search]);
  const filteredSaved = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return savedQueries;
    return savedQueries.filter((item) => [item.name, item.description, item.category, item.queryText, ...(item.labels ?? [])].join(' ').toLowerCase().includes(needle));
  }, [savedQueries, search]);
  const filteredHistory = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const sourceFiltered = historyItems.filter((item) => item.source === source && (!customerId || item.customerId === customerId));
    if (!needle) return sourceFiltered;
    return sourceFiltered.filter((item) => [item.title, item.customerName, item.queryText, item.timespan].join(' ').toLowerCase().includes(needle));
  }, [customerId, historyItems, search, source]);

  if (collapsed) {
    return (
      <aside className="shrink-0 rounded-2xl border border-border bg-card shadow p-2 h-fit sticky top-4">
        <button
          type="button"
          onClick={() => onCollapsedChange(false)}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold shadow hover:opacity-90"
          title="Open query explorer"
        >
          ☰
        </button>
        <div className="mt-2 flex flex-col gap-1">
          {visibleSections.map((section) => (
            <button
              type="button"
              key={section.id}
              onClick={() => { setActiveSection(section.id); onCollapsedChange(false); }}
              className="h-9 w-10 rounded-lg border border-border bg-background text-[10px] font-bold text-muted-foreground hover:bg-muted"
              title={section.label}
            >
              {section.label.slice(0, 1)}
            </button>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside className="shrink-0 w-full lg:w-[330px] xl:w-[360px] rounded-2xl border border-border bg-card shadow h-fit lg:sticky lg:top-4 overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-3">
        <div className="min-w-0">
          <div className="text-sm font-bold text-foreground">Query Explorer</div>
          <div className="truncate text-[11px] text-muted-foreground">{sourceLabel(source)} · {customerName || customerId || 'No customer'}</div>
        </div>
        <button
          type="button"
          onClick={() => onCollapsedChange(true)}
          className="rounded-lg border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-muted"
          title="Collapse query explorer"
        >
          ◀
        </button>
      </div>

      <div className="border-b border-border p-2">
        <div className="grid grid-cols-2 gap-1">
          {visibleSections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${activeSection === section.id ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {section.label}
              <span className="ml-1 opacity-70">{section.count}</span>
            </button>
          ))}
        </div>
        <input
          className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search explorer..."
        />
      </div>

      <div className="max-h-[72vh] overflow-y-auto p-2">
        {visibleSections.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
            Query Explorer is disabled for your role. Enable the Advanced Hunting explorer permissions in Permissions Master.
          </div>
        ) : null}

        {activeSection === 'tables' && canViewTables && (
          <div className="space-y-1">
            <SectionHeader title="Tables" count={filteredTables.length} />
            {filteredTables.map((item) => (
              <ItemButton key={item.id} item={item} onClick={() => onLoadQuery({ queryText: item.queryText, source: item.source, queryExecutionMode: 'standard', timeRangeMode: 'lookback', name: item.title })} />
            ))}
            {filteredTables.length === 0 ? <EmptyState text="No tables match your search." /> : null}
          </div>
        )}

        {activeSection === 'functions' && (canViewFunctions || canViewSavedFunctions) && (
          <div className="space-y-1">
            <SectionHeader title="Functions" count={filteredFunctions.length}>
              {functionsLoading ? <span className="text-[10px] text-muted-foreground">Loading saved...</span> : null}
            </SectionHeader>
            {functionsError ? <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">{functionsError}</div> : null}
            {filteredFunctions.map((item) => (
              <div key={item.id} className="rounded-lg hover:bg-muted">
                <ItemButton item={item} onClick={() => onLoadQuery({ queryText: item.queryText, source: item.source, queryExecutionMode: 'standard', timeRangeMode: 'in_query', name: item.title })} />
                <div className="px-2 pb-2 text-[11px] text-muted-foreground">{item.description}</div>
              </div>
            ))}
            {filteredFunctions.length === 0 ? <EmptyState text="No functions match your search." /> : null}
          </div>
        )}

        {activeSection === 'saved' && canViewSavedQueries && (
          <div className="space-y-1">
            <SectionHeader title="Saved Queries" count={filteredSaved.length}>
              {savedLoading ? <span className="text-[10px] text-muted-foreground">Loading...</span> : null}
            </SectionHeader>
            {savedError ? <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">{savedError}</div> : null}
            {filteredSaved.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onLoadQuery({
                  queryText: item.queryText,
                  source: item.source,
                  queryExecutionMode: item.queryExecutionMode ?? 'standard',
                  timeRangeMode: item.timeRangeMode ?? 'lookback',
                  timespan: item.timespan,
                  name: item.name,
                })}
                className="w-full rounded-lg px-2 py-2 text-left hover:bg-muted transition-colors"
                title={item.queryText}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-foreground">{item.name}</div>
                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {(item.category || item.visibility || sourceLabel(item.source))}{item.updatedAtUtc ? ` · ${formatDateTime(item.updatedAtUtc)}` : ''}
                    </div>
                    {item.labels && item.labels.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.labels.slice(0, 3).map((label) => <span key={label} className="rounded-full bg-muted-foreground/10 px-1.5 py-0.5 text-[10px] text-muted-foreground">{label}</span>)}
                      </div>
                    ) : null}
                  </div>
                  <span className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">saved</span>
                </div>
              </button>
            ))}
            {!savedLoading && filteredSaved.length === 0 ? <EmptyState text="No saved queries found for this source/customer." /> : null}
          </div>
        )}

        {activeSection === 'history' && canViewQueryHistory && (
          <div className="space-y-1">
            <SectionHeader title="Query History" count={filteredHistory.length}>
              {historyLoading ? <span className="text-[10px] text-muted-foreground">Loading...</span> : null}
              {historyItems.length > 0 && (canManageQueryHistory || serverHistory.length === 0) ? (
                <button type="button" onClick={() => void handleClearHistory()} className="text-[10px] font-semibold text-red-600 hover:underline">Clear</button>
              ) : null}
            </SectionHeader>
            {historyError ? <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">{historyError}</div> : null}
            {filteredHistory.map((item) => (
              <div key={item.id} className="group rounded-lg px-2 py-2 hover:bg-muted transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => onLoadQuery({
                      queryText: item.queryText,
                      source: item.source,
                      queryExecutionMode: item.queryExecutionMode,
                      timeRangeMode: item.timeRangeMode,
                      timespan: item.timespan,
                      limit: item.limit,
                      name: item.title,
                    })}
                    title={item.queryText}
                  >
                    <div className="truncate text-xs font-semibold text-foreground">{item.title}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {formatDateTime(item.ranAtUtc)} · {item.rowCount ?? 0} rows{item.durationMs ? ` · ${item.durationMs} ms` : ''}
                    </div>
                    <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground/90">{shortQuery(item.queryText)}</div>
                  </button>
                  {(canManageQueryHistory || !serverHistory.some((serverItem) => serverItem.id === item.id)) ? (
                    <button
                      type="button"
                      onClick={() => void handleDeleteHistoryItem(item.id)}
                      className="opacity-0 group-hover:opacity-100 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-red-600"
                      title="Remove from history"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {filteredHistory.length === 0 ? <EmptyState text="No query history yet for this source." /> : null}
          </div>
        )}
      </div>
    </aside>
  );
}

function filterItems<T extends ExplorerItem>(items: T[], search: string): T[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((item) => [item.title, item.subtitle, item.queryText, item.badge].join(' ').toLowerCase().includes(needle));
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
      {text}
    </div>
  );
}
